import React from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { Badge } from "@/components/ui/Badge";
import { Modal as UIModal } from "@/components/ui/Modal";
import { formatRelativeTime, formatAbsoluteDate } from "./Shared";
import type { FieldEditInfo } from "@/lib/api/review";
import type { TranslationKeys, SupportedLocale } from "@chinese-vendor/i18n";

export interface DetailFieldEditModalProps {
  visible: boolean;
  onDismiss: () => void;
  titleKey?: TranslationKeys;
  data?: FieldEditInfo;
  t: ReturnType<typeof import("@/context/I18nContext").useI18n>["t"];
  locale: SupportedLocale;
}

export function DetailFieldEditModal({
  visible,
  onDismiss,
  titleKey,
  data,
  t,
  locale,
}: DetailFieldEditModalProps) {
  return (
    <UIModal
      visible={visible}
      onDismiss={onDismiss}
      title={titleKey ?? null}
      closeOnBackdrop
      useScrollView
    >
      {/* EDITED BY */}
      <View className="gap-2 py-4">
        <AppText className="text-xs font-semibold text-supporting-text-40 tracking-widest uppercase">
          {t("review.review.fieldState.showEditModal.editedBy")}
        </AppText>
        <View className="gap-2">
          <AppText weight="medium" className="text-dark-text-50">
            {data?.editedBy?.name ?? "—"}
          </AppText>
          {data?.editedBy?.role && <Badge variant="accent-blue" label={data.editedBy.role} />}
        </View>
      </View>

      {/* Divider */}
      <View className="h-px bg-stroke-20" />

      {/* DATE EDITED */}
      <View className="gap-2 py-4">
        <AppText className="text-xs font-semibold text-supporting-text-40 tracking-widest uppercase">
          {t("review.review.fieldState.showEditModal.dateEdited")}
        </AppText>
        {data?.editedAt ? (
          <View className="gap-1">
            <AppText weight="bold" className="text-dark-text-50">
              {formatRelativeTime(data.editedAt, locale, t)}
            </AppText>
            <AppText className="text-sm text-supporting-text">
              {formatAbsoluteDate(data.editedAt, locale)}
            </AppText>
          </View>
        ) : (
          <AppText weight="medium" className="text-dark-text-50">
            —
          </AppText>
        )}
      </View>

      {/* Divider */}
      <View className="h-px bg-stroke-20" />

      {/* CURRENT VALUE */}
      <View className="gap-2 py-4">
        <AppText className="text-xs font-semibold text-supporting-text-40 tracking-widest uppercase">
          {t("review.review.fieldState.showEditModal.currentValue")}
        </AppText>
        <AppText weight="medium" className="text-dark-text-50">
          {data?.currentValue ?? "—"}
        </AppText>
      </View>

      {/* Divider */}
      <View className="h-px bg-stroke-20" />

      {/* PREVIOUS VALUE */}
      <View className="gap-2 py-4">
        <AppText className="text-xs font-semibold text-supporting-text-40 tracking-widest uppercase">
          {t("review.review.fieldState.showEditModal.previousValue")}
        </AppText>
        <AppText weight="medium" className="text-dark-text-50">
          {data?.previousValue ?? "—"}
        </AppText>
      </View>
    </UIModal>
  );
}
